import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { Company } from '../types';
import { companyApi } from '../services/api';

interface CompanyContextType {
  selectedCompanyId: string | null;
  companies: Company[];
  isLoading: boolean;
  setSelectedCompanyId: (companyId: string | null) => void;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load companies when user is authenticated and is eskimi_admin, adops, or admin
  useEffect(() => {
    if (isAuthenticated && user && (user.role === 'eskimi_admin' || user.role === 'adops' || user.role === 'admin')) {
      loadCompanies();
    } else if (!isAuthenticated || !user) {
      // Clear companies when not authenticated
      setCompanies([]);
      setSelectedCompanyIdState(null);
    } else {
      // For regular users, set their company as selected
      if (user.companyId) {
        setSelectedCompanyIdState(user.companyId);
        // Load just their company
        loadUserCompany(user.companyId);
      }
    }
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      const companiesData = await companyApi.getAll();
      setCompanies(companiesData);

      // Auto-select user's company if they have one and nothing is selected yet
      if (user?.companyId && !selectedCompanyId) {
        setSelectedCompanyIdState(user.companyId);
        // Save to localStorage
        localStorage.setItem('selectedCompanyId', user.companyId);
      } else {
        // Try to restore from localStorage
        const savedCompanyId = localStorage.getItem('selectedCompanyId');
        if (savedCompanyId && companiesData.some(c => c.id === savedCompanyId)) {
          setSelectedCompanyIdState(savedCompanyId);
        } else if (user?.companyId) {
          setSelectedCompanyIdState(user.companyId);
          localStorage.setItem('selectedCompanyId', user.companyId);
        }
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserCompany = async (companyId: string) => {
    try {
      setIsLoading(true);
      const company = await companyApi.getById(companyId);
      setCompanies([company]);
    } catch (error) {
      console.error('Failed to load user company:', error);
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  const setSelectedCompanyId = (companyId: string | null) => {
    setSelectedCompanyIdState(companyId);
    if (companyId) {
      localStorage.setItem('selectedCompanyId', companyId);
    } else {
      localStorage.removeItem('selectedCompanyId');
    }
  };

  const refreshCompanies = async () => {
    await loadCompanies();
  };

  const value: CompanyContextType = {
    selectedCompanyId,
    companies,
    isLoading,
    setSelectedCompanyId,
    refreshCompanies,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
